import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import feather from 'feather-icons';
import { Link, useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import { amountDisplay } from '../util/helper';
import { getAccountImage } from '../util/transactionHelper';
import AddEditAccountBottomSheet from '../components/Modals/AddEditAccountBottomSheet';
import SuccessToast from '../toast/SuccessToast';
import Loader from '../components/Loader';
import BottomNavbar from '../components/Home/BottomNavbar';
import Alert from '../components/Alert';
import StatisticsBanner from '../components/Accounts/StatisticsBannerComponent';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';

// Skeleton Loading Component
const AccountSkeleton = () => (
    <div className="col-6">
        <div className="saving-plan-box skeleton-card">
            <div
                className="skeleton-loading"
                style={{ height: '20px', width: '70%', marginBottom: '10px' }}
            ></div>
            <div
                className="skeleton-loading"
                style={{ height: '15px', width: '50%', marginBottom: '10px' }}
            ></div>
            <div className="skeleton-loading" style={{ height: '15px', width: '80%' }}></div>
        </div>
    </div>
);

// Header Component - Reverted to Original Design
const Header = ({ onBackClick, handleShow, t }) => (
    <header className="section-t-space">
        <div className="custom-container">
            <div className="header-panel">
                <button className="back-btn" onClick={onBackClick}>
                    <i className="icon" data-feather="arrow-left"></i>
                </button>
                <h2>{t('myAccounts')}</h2>
                <button className="back-btn" onClick={() => handleShow(null)}>
                    <i className="icon" data-feather="plus"></i>
                </button>
            </div>
        </div>
    </header>
);

// Account Card Component - Improved with Icon
const AccountBox = ({ account, language, t }) => (
    <div className="col-6">
        <div className="saving-plan-box">
            <Link to={`/account/${account.id}`} key={account.id} style={{ textDecoration: 'none' }}>
                <div className="account-box-header">
                    <div className="account-icon-wrapper">
                        <img src={getAccountImage(account.id)} alt={account.name} loading="lazy" />
                    </div>
                    <h4 style={{ margin: 0 }}>{account?.name}</h4>
                </div>

                <h3 className="pt-1">{t(account?.type)}</h3>
                <h6 className={account?.balance < 0 ? 'mt-2 text-danger' : 'mt-2'}>
                    {t('balance')}: <b>{amountDisplay(account?.balance, language)}</b>
                </h6>
            </Link>
        </div>
    </div>
);

// Empty State Component
const EmptyAccountState = ({ handleShow, t }) => (
    <div className="empty-accounts-state text-center mt-5">
        <div className="empty-state-content">
            <i className="empty-icon" data-feather="briefcase"></i>
            <h3 className="empty-title">{t('noAccounts')}</h3>
            <p className="empty-description">{t('createFirstAccount')}</p>
            <button
                onClick={() => handleShow(null)}
                className="btn btn-primary mt-3"
                aria-label={t('addAccount')}
            >
                {t('addAccount')}
            </button>
        </div>
    </div>
);

// Account List Section Component
const AccountListSection = ({ accounts, language, t }) => (
    <section className="section-b-space">
        <div className="custom-container">
            <div className="row gy-3">
                {accounts.map((account) => (
                    <AccountBox key={account.id} account={account} language={language} t={t} />
                ))}
            </div>
        </div>
    </section>
);

// Main Component
const AccountList = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();

    // State Management
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accounts, setAccounts] = useState(null);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [showToastForDelete, setShowToastForDelete] = useState(false);

    // Calculate total balance using useMemo for optimization
    const totalBalance = useMemo(() => {
        if (!accounts || accounts.length === 0) return 0;
        return accounts.reduce((sum, account) => sum + (account.balance || 0), 0);
    }, [accounts]);

    // Fetch accounts from API
    const fetchAccounts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await serverClient.get('/api/user/accounts');
            setAccounts(response?.data?.accounts || []);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);

    // Handle modal show
    const handleShow = useCallback(() => {
        setSelectedAccount(null);
        setModalMode('add');
        setShowModal(true);
    }, []);

    // Handle modal close
    const handleClose = useCallback(() => {
        setSelectedAccount(null);
        setShowModal(false);
    }, []);

    // Handle account added or updated
    const handleAccountAddedOrUpdated = useCallback(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    // Handle toast close
    const handleToastClose = useCallback(() => setShowToast(false), []);

    // Initialize feather icons only once
    useEffect(() => {
        feather.replace();
    }, []);

    // Fetch accounts on mount
    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    // Refresh when signal changes (skips initial mount)
    useRefreshSignal(refreshSignal, () => {
        fetchAccounts();
    });

    return (
        <div>
            <Helmet>
                <title>{getPageMeta('accounts', language).title}</title>
                <meta name="description" content={getPageMeta('accounts', language).description} />
                <meta name="keywords" content={getPageMeta('accounts', language).keywords} />
                <link rel="canonical" href={getPageMeta('accounts', language).url} />
                {getPageMeta('accounts', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('accounts', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('accounts', language).description}
                />
                <meta property="og:url" content={getPageMeta('accounts', language).url} />
                <meta property="og:image" content={getPageMeta('accounts', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('accounts', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('accounts', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('accounts', language).image} />
            </Helmet>
            <Header onBackClick={() => navigate('/')} handleShow={handleShow} t={t} />

            {/* Error Alert */}
            {error && (
                <section className="error-section">
                    <div className="custom-container">
                        <Alert alert={'danger'}>{error}</Alert>
                    </div>
                </section>
            )}

            {/* Loading State */}
            <Loader loading={loading} />

            {/* Total Balance Banner */}
            {accounts && <StatisticsBanner totalSaving={totalBalance} />}

            {/* Main Content */}
            {loading && accounts === null ? (
                <section className="section-b-space">
                    <div className="custom-container">
                        <div className="row gy-3">
                            {[...Array(3)].map((_, index) => (
                                <AccountSkeleton key={`skeleton-${index}`} />
                            ))}
                        </div>
                    </div>
                </section>
            ) : accounts && accounts.length > 0 ? (
                <AccountListSection accounts={accounts} language={language} t={t} />
            ) : (
                <EmptyAccountState handleShow={handleShow} t={t} />
            )}

            {/* Add/Edit Account Modal */}
            <AddEditAccountBottomSheet
                show={showModal}
                onClose={handleClose}
                onAccountAction={handleAccountAddedOrUpdated}
                mode={modalMode}
                accountData={selectedAccount}
                showToast={showToast}
                setShowToast={setShowToast}
                showToastForDelete={showToastForDelete}
                setShowToastForDelete={setShowToastForDelete}
            />

            {/* Success Toast */}
            {showToast && <SuccessToast message={t('accountUpdated')} onClose={handleToastClose} />}

            {/* Bottom Navigation */}
            <BottomNavbar />
        </div>
    );
};

export default AccountList;
